import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Phone,
  Edit,
  Trash2,
  Calendar,
  Clock,
  CheckSquare,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LeadFormModal } from '../components/crm/LeadFormModal';
import { ActivityList } from '../components/crm/ActivityList';
import { ActivityForm } from '../components/crm/ActivityForm';
import { TagSelect } from '../components/crm/TagSelect';
import { CreateTodoModal } from '../components/todos';
import {
  getLeadById,
  getTags,
  updateLead,
  updateLeadStatus,
  deleteLead,
  createTag,
  getActivities,
  createActivity,
} from '../services/leadService';
import { createTodo } from '../services/todoService';
import { LeadSubmissions } from '../components/forms/LeadSubmissions';
import { Toast } from '../components/ui';
import type {
  LeadWithTags,
  LeadTag,
  LeadActivity,
  LeadStatus,
  ActivityType,
  UpdateLeadInput,
} from '../types/crm';
import type { CreateTodoInput } from '../types/todo';
import { logger } from '../lib/logger';

const STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: 'NEW', label: 'Neu' },
  { value: 'CONTACTED', label: 'Kontaktiert' },
  { value: 'IN_DISCUSSION', label: 'In Gespräch' },
  { value: 'WON', label: 'Gewonnen' },
  { value: 'LOST', label: 'Verloren' },
];

const STATUS_STYLES: Record<LeadStatus, string> = {
  NEW: 'bg-blue-50 text-blue-700 border-blue-200',
  CONTACTED: 'bg-amber-50 text-amber-700 border-amber-200',
  IN_DISCUSSION: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  WON: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  LOST: 'bg-gray-100 text-gray-600 border-gray-200',
};

const TYPE_STYLES = {
  CUSTOMER: 'bg-primary/10 text-primary',
  PARTNER: 'bg-rose-50 text-rose-700',
};

const TYPE_LABELS = {
  CUSTOMER: 'Kunde',
  PARTNER: 'Partner',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [lead, setLead] = useState<LeadWithTags | null>(null);
  const [tags, setTags] = useState<LeadTag[]>([]);
  const [activities, setActivities] = useState<LeadActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActivitiesLoading, setIsActivitiesLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isTodoModalOpen, setIsTodoModalOpen] = useState(false);
  const [showTodoToast, setShowTodoToast] = useState(false);

  const loadLead = useCallback(async () => {
    if (!user || !id) return;
    setIsLoading(true);
    try {
      const result = await getLeadById(id, user.id);
      if (!result) {
        navigate('/leads');
        return;
      }
      setLead(result);
    } catch (error) {
      logger.error('Failed to load lead:', error);
      navigate('/leads');
    } finally {
      setIsLoading(false);
    }
  }, [user, id, navigate]);

  const loadTags = useCallback(async () => {
    if (!user) return;
    try {
      const result = await getTags(user.id);
      setTags(result);
    } catch (error) {
      logger.error('Failed to load tags:', error);
    }
  }, [user]);

  const loadActivities = useCallback(async () => {
    if (!user || !id) return;
    setIsActivitiesLoading(true);
    try {
      const result = await getActivities(id, user.id);
      setActivities(result);
    } catch (error) {
      logger.error('Failed to load activities:', error);
    } finally {
      setIsActivitiesLoading(false);
    }
  }, [user, id]);

  useEffect(() => {
    loadLead();
    loadTags();
    loadActivities();
  }, [loadLead, loadTags, loadActivities]);

  const handleUpdateLead = async (data: UpdateLeadInput) => {
    if (!user || !id) return;
    await updateLead(id, user.id, data);
    await loadLead();
  };

  const handleStatusChange = async (newStatus: LeadStatus) => {
    if (!user || !id || !lead || lead.status === newStatus) return;
    await updateLeadStatus(id, user.id, lead.status, newStatus);
    await loadLead();
    await loadActivities();
  };

  const handleTagsChange = async (tagIds: string[]) => {
    if (!user || !id) return;
    await updateLead(id, user.id, { tag_ids: tagIds });
    await loadLead();
  };

  const handleCreateTag = async (name: string): Promise<LeadTag> => {
    if (!user) throw new Error('Not authenticated');
    const newTag = await createTag(user.id, name);
    setTags((prev) => [...prev, newTag].sort((a, b) => a.name.localeCompare(b.name)));
    return newTag;
  };

  const handleAddActivity = async (type: ActivityType, content: string) => {
    if (!user || !id) return;
    await createActivity(user.id, { lead_id: id, type, content });
    await loadActivities();
    await loadLead();
  };

  const handleDelete = async () => {
    if (!user || !id) return;
    setIsDeleting(true);
    try {
      await deleteLead(id, user.id);
      navigate('/leads');
    } catch (error) {
      logger.error('Failed to delete lead:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateTodo = async (input: CreateTodoInput) => {
    if (!user) return;
    await createTodo(user.id, input);
    setIsTodoModalOpen(false);
    setShowTodoToast(true);
  };

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!lead) {
    return null;
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      {showTodoToast && (
        <Toast
          message="Todo erfolgreich erstellt!"
          linkText="Zu den Todos"
          linkTo="/todos"
          onClose={() => setShowTodoToast(false)}
        />
      )}

      <button
        onClick={() => navigate('/leads')}
        className="inline-flex items-center gap-2 text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        Zurück zu Leads
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="min-w-0 flex-1 mr-2">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 break-words">
                  {lead.full_name}
                </h1>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-medium ${TYPE_STYLES[lead.lead_type]}`}
                  >
                    {TYPE_LABELS[lead.lead_type]}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
                  title="Bearbeiten"
                >
                  <Edit className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                  title="Löschen"
                >
                  <Trash2 className="w-5 h-5 text-red-500" />
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Status
              </label>
              <select
                value={lead.status}
                onChange={(e) => handleStatusChange(e.target.value as LeadStatus)}
                className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary font-medium ${STATUS_STYLES[lead.status]}`}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-4">
              {lead.email && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-slate-700 rounded-xl flex items-center justify-center">
                    <Mail className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-slate-400">E-Mail</p>
                    <a
                      href={`mailto:${lead.email}`}
                      className="text-sm text-gray-900 dark:text-white hover:text-primary"
                    >
                      {lead.email}
                    </a>
                  </div>
                </div>
              )}

              {lead.phone && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 dark:bg-slate-700 rounded-xl flex items-center justify-center">
                    <Phone className="w-5 h-5 text-gray-500 dark:text-slate-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Telefon</p>
                    <a
                      href={`tel:${lead.phone}`}
                      className="text-sm text-gray-900 dark:text-white hover:text-primary"
                    >
                      {lead.phone}
                    </a>
                  </div>
                </div>
              )}

              {lead.source && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">Quelle</p>
                  <p className="text-sm text-gray-900 dark:text-white">{lead.source}</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Tags</h3>
            <TagSelect
              tags={tags}
              selectedTagIds={lead.tags.map((t) => t.id)}
              onChange={handleTagsChange}
              onCreateTag={handleCreateTag}
            />
          </div>

          <button
            onClick={() => setIsTodoModalOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300 rounded-xl transition-colors font-medium"
          >
            Todo erstellen
            <CheckSquare className="w-5 h-5" />
          </button>

          {lead.notes && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Notizen</h3>
              <p className="text-sm text-gray-700 dark:text-slate-300 whitespace-pre-wrap">{lead.notes}</p>
            </div>
          )}

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Details</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                <span className="text-gray-500 dark:text-slate-400">Erstellt:</span>
                <span className="text-gray-900 dark:text-white">{formatDate(lead.created_at)}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Clock className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                <span className="text-gray-500 dark:text-slate-400">Letzte Aktivität:</span>
                <span className="text-gray-900 dark:text-white">
                  {lead.last_activity_at ? formatDate(lead.last_activity_at) : '-'}
                </span>
              </div>
            </div>
          </div>

          {user && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Formulareinreichungen</h3>
              <LeadSubmissions leadId={lead.id} userId={user.id} />
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Aktivitäten</h2>
            <div className="mb-6">
              <ActivityForm onSubmit={handleAddActivity} />
            </div>
            <div className="border-t border-gray-100 dark:border-slate-700 pt-6">
              <ActivityList activities={activities} isLoading={isActivitiesLoading} />
            </div>
          </div>
        </div>
      </div>

      <LeadFormModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleUpdateLead}
        lead={lead}
        tags={tags}
        onCreateTag={handleCreateTag}
      />

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center !mt-0">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Lead löschen?
            </h3>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              Möchtest du "{lead.full_name}" wirklich löschen? Diese Aktion kann nicht
              rückgängig gemacht werden.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors font-medium"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
              >
                {isDeleting ? 'Löschen...' : 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CreateTodoModal
        isOpen={isTodoModalOpen}
        onClose={() => setIsTodoModalOpen(false)}
        onSubmit={handleCreateTodo}
        defaultTitle={`Lead: ${lead.full_name}`}
        defaultDescription="Per WhatsApp kontaktieren"
        tag="lead"
        sourceType="lead"
        sourceId={lead.id}
      />
    </div>
  );
}
